import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { DragOverEvent } from '@dnd-kit/core'
import {
    restrictToParentElement,
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
    Backdrop,
    CircularProgress,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import React, {
    FC,
    memo,
    MouseEventHandler,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'

import SortableComponent from 'bpm/components/sortable/SortableComponent'
import SortableItem from 'bpm/components/sortable/SortableItem'
import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'

import DeleteButton from '../components/DeleteButton'
import {
    ERROR_CANNOT_REMOVE_PROPERTY,
    PROMPT_DELETE_PROPERTY,
    TITLE_DELETE_PREMIUM_PROPERTY,
} from '../constants'
import { PPL_ACTIONS, usePPLContext } from '../PPLProvider'

import { useApolloClient, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GetIsManufacturerOptionDetailsRemovable } from 'bpm/api/graphQL/queries/product.queries'
import { ProductContext, ProductContextType } from '../../../ListingDetailsPage'
import NonMSEditable from './editNonMS/NonMSEditableProperty'
import NonMSModeContainer from './NonMSModeContainer'
import { insertToIndex } from './nonMSReducer'
import { NonMSPropertyType } from './nonMSTypes'
import NonMSPropertySection from './viewNonMS/NonMSPropertySection'

const { buttonSecondary } = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    nonMsContainer: {
        width: '100vw',
    },
    propertyContainer: {
        width: '100% ',
        display: 'flex',
        cursor: 'default',
    },
    deleteContainer: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(0.75),
    },
}))

const NonMSContainer: FC = () => {
    const classes = useStyles()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [isRemoveLoading, setIsRemoveLoading] = useState(false)
    const client = useApolloClient()
    const {
        msProperties: { pPlusItems },
        nonMSProperties: state,
        dispatch,
    } = usePPLContext()

    const msHeaders = useMemo(() => Object.keys(pPlusItems) ?? [], [])

    const { product } = useContext<Partial<ProductContextType>>(ProductContext)

    const properties: Array<NonMSPropertyType> = useMemo(
        () => state?.properties ?? [],
        [state]
    )

    const handleDragEnd = useCallback(
        (drag: DragOverEvent) => {
            const { active, over } = drag
            if (!!active && !!over && active.id !== over.id) {
                const oldItem = properties.findIndex(
                    (item: NonMSPropertyType) =>
                        item.dummyPropertyId === active.id
                )
                const newItem = properties.findIndex(
                    (item: NonMSPropertyType) =>
                        item.dummyPropertyId === over.id
                )
                dispatch({
                    type: PPL_ACTIONS.setProperties,
                    payload: arrayMove(properties, oldItem, newItem),
                })
            }
        },
        [dispatch, properties]
    )

    const handlePropertyEdit = useCallback(
        (index) => () => {
            dispatch({
                type: PPL_ACTIONS.batchNonMSActions,
                payload: [
                    { type: PPL_ACTIONS.setMode, payload: '' },
                    { type: PPL_ACTIONS.setPrefixEditable, payload: false },
                    { type: PPL_ACTIONS.setEditPropertyID, payload: index },
                ],
            })
        },
        [dispatch]
    )

    const handleDeleteProperty = useCallback(
        (dummyPropertyId): MouseEventHandler => (e) => {
            e.stopPropagation()

            return NavPromptDialogContainer(
                JSON.stringify({
                    title: TITLE_DELETE_PREMIUM_PROPERTY,
                    preferredMessage: PROMPT_DELETE_PROPERTY,
                    buttonColor: buttonSecondary,
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                async (callbackState) => {
                    if (callbackState) {
                        setIsRemoveLoading(true)
                        const property = properties.find(
                            (property: NonMSPropertyType) =>
                                property.dummyPropertyId === dummyPropertyId
                        )
                        if (property) {
                            const details = property.subProperties.reduce(
                                (
                                    acc: Array<{ propertyId: string }>,
                                    { propertyId }
                                ) => {
                                    acc.push({ propertyId })
                                    return acc
                                },
                                []
                            )

                            const {
                                data: {
                                    getManufacturerOptionDetails: {
                                        manufacturerOptionGroupDetails,
                                    },
                                },
                            } = await client.query({
                                query: GetIsManufacturerOptionDetailsRemovable,
                                variables: {
                                    input: {
                                        listingId: product?.listingId ?? '',
                                        details,
                                    },
                                },
                            })

                            if (
                                manufacturerOptionGroupDetails.some(
                                    (result: Record<string, string>) =>
                                        !result.isDetailsRemovable
                                )
                            ) {
                                let nonRemovable = ''
                                let removable = ''

                                manufacturerOptionGroupDetails.forEach(
                                    (result: Record<string, string>) => {
                                        if (!!result.isDetailsRemovable) {
                                            const removableProp = property.subProperties.find(
                                                (prop) =>
                                                    prop.propertyId ===
                                                    result.propertyId
                                            )

                                            removable = removable.length
                                                ? `${removable} ,${removableProp?.subPropertyName}`
                                                : removableProp?.subPropertyName ??
                                                  ''

                                            dispatch({
                                                type: PPL_ACTIONS.setProperties,
                                                payload: insertToIndex(
                                                    properties,
                                                    properties.findIndex(
                                                        (prop) =>
                                                            prop.dummyPropertyId ===
                                                            dummyPropertyId
                                                    ),
                                                    {
                                                        ...property,
                                                        subProperties: property.subProperties.filter(
                                                            (subProp) =>
                                                                subProp.propertyId !==
                                                                result.propertyId
                                                        ),
                                                    },
                                                    true
                                                ),
                                            })
                                        } else {
                                            const nonRemovableProp = property.subProperties.find(
                                                (prop) =>
                                                    prop.propertyId ===
                                                    result.propertyId
                                            )
                                            nonRemovable = nonRemovable.length
                                                ? `${nonRemovable} ,${nonRemovableProp?.subPropertyName}`
                                                : nonRemovableProp?.subPropertyName ??
                                                  ''
                                        }
                                    }
                                )

                                showGlobalSnackbar({
                                    variables: {
                                        message: ERROR_CANNOT_REMOVE_PROPERTY({
                                            removable,
                                            nonRemovable,
                                        }),
                                        variant: 'error',
                                        id: 'RemovePropertyFailed',
                                    },
                                })
                                setIsRemoveLoading(false)
                            } else {
                                setIsRemoveLoading(false)
                                return dispatch({
                                    type: PPL_ACTIONS.setProperties,
                                    payload: properties.filter(
                                        (property: NonMSPropertyType) =>
                                            property.dummyPropertyId !==
                                            dummyPropertyId
                                    ),
                                })
                            }
                        }
                    }
                }
            )
        },
        [properties, dispatch]
    )

    const ChildRenderer = memo(
        ({
            index,
            prop,
            activeId,
        }: {
            index?: number
            prop?: NonMSPropertyType
            activeId?: string
        }) => {
            let property = prop
            let propertyIndex = index

            if (!property && !propertyIndex && !!activeId) {
                propertyIndex = properties.findIndex(
                    (prop: NonMSPropertyType) =>
                        prop.dummyPropertyId === activeId
                )
                property = properties[propertyIndex]
            }

            if (
                !property ||
                propertyIndex === undefined ||
                propertyIndex === null
            ) {
                return <></>
            }

            return (
                <Typography
                    component="span"
                    className={classes.propertyContainer}
                >
                    <NonMSPropertySection
                        property={property}
                        propIndex={propertyIndex}
                        handlePropertyEdit={handlePropertyEdit(index)}
                    />
                    <Typography
                        component="span"
                        className={classes.deleteContainer}
                    >
                        <DeleteButton
                            data-testid="delete-mo-property-button"
                            onClick={handleDeleteProperty(
                                property.dummyPropertyId
                            )}
                            disabled={false}
                        />
                    </Typography>
                </Typography>
            )
        }
    )

    return (
        <GridItem xs={12} className={classes.nonMsContainer}>
            <NonMSModeContainer />
            <SortableComponent
                onDragEnd={handleDragEnd}
                sortableItems={properties.map(
                    (item: NonMSPropertyType, index: number) =>
                        item.dummyPropertyId ?? index
                )}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                strategy={verticalListSortingStrategy}
                ActiveComponent={ChildRenderer}
            >
                {properties.map((prop: NonMSPropertyType, index: number) =>
                    !!msHeaders.includes(prop.propertyName)!! ||
                    !prop.propertyName ? (
                        state.editablePropertyId === index ? (
                            <NonMSEditable property={prop} />
                        ) : (
                            <ChildRenderer index={index} prop={prop} />
                        )
                    ) : (
                        <SortableItem
                            id={prop.dummyPropertyId}
                            handle={
                                properties.length > 1 &&
                                state.editablePropertyId !== index
                            }
                            disabled={
                                state.editablePropertyId === index ||
                                properties.length < 2
                            }
                            handleProps={{ size: 'big', panleft: 'true' }}
                            key={`${prop.dummyPropertyId}_${index}`}
                        >
                            {state.editablePropertyId === index ? (
                                <NonMSEditable property={prop} />
                            ) : (
                                <ChildRenderer index={index} prop={prop} />
                            )}
                        </SortableItem>
                    )
                )}
            </SortableComponent>

            {isRemoveLoading && (
                <Backdrop className={classes.backdrop} open={isRemoveLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </GridItem>
    )
}

export default NonMSContainer
